// Alpine-component voor de reisassistent-chat (Fase 2: tools, tool-chips, bevestigingskaart;
// Fase 4: markdown-rendering, stop-knop, smart autoscroll, lege-staat, focus-after-send).
import { renderMarkdown } from './markdown.js';

export function reisassistent({ gesprekId, initialMessages = [], csrf = '' } = {}) {
  return {
    gesprekId,
    csrf,
    // items: {role:'user'|'assistant', text}
    //      | {role:'tool', id, name, status:'bezig'|'klaar'|'fout'}
    //      | {role:'confirm', tool_call_id, confirm_token, tool_naam, preview, busy:false, done:false, result:null}
    messages: initialMessages,
    input: '',
    streaming: false,
    error: '',
    streamIdx: null,      // index van de huidige open assistent-bel
    toolChipIdx: {},      // tool_use_id → index in messages
    abortCtrl: null,      // AbortController voor de lopende fetch
    thinking: false,      // toon "denkt na…"-indicator
    creditsLaag: null,    // aantal resterende credits als < 5, anders null

    init() {
      this.$nextTick(() => this.scrollDown());
    },

    // ── Markdown rendering ───────────────────────────────────────────────
    renderMd(text) {
      return renderMarkdown(text || '');
    },

    // ── Scroll helpers ───────────────────────────────────────────────────
    nearBottom() {
      const el = this.$refs.log;
      return !el || (el.scrollHeight - el.scrollTop - el.clientHeight) < 80;
    },

    scrollDown() {
      const el = this.$refs.log;
      if (el) el.scrollTop = el.scrollHeight;
    },

    // ── Stop-knop ────────────────────────────────────────────────────────
    stop() {
      this.abortCtrl?.abort();
    },

    // ── Lege-staat voorbeeldzinnen ────────────────────────────────────────
    gebruikVoorbeeld(zin) {
      this.input = zin;
      this.$nextTick(() => this.$refs.composer?.focus());
    },

    handleEvent(ev) {
      // Verberg de "denkt na…"-indicator zodra er echte inhoud binnenkomt
      if (ev.type !== 'thinking') {
        this.thinking = false;
      }

      if (ev.type === 'thinking') {
        this.thinking = true;
      } else if (ev.type === 'text_delta') {
        if (this.streamIdx === null || !this.messages[this.streamIdx] || this.messages[this.streamIdx].role !== 'assistant') {
          this.messages.push({ role: 'assistant', text: '' });
          this.streamIdx = this.messages.length - 1;
        }
        const wasNear = this.nearBottom();
        this.messages[this.streamIdx].text += ev.text;
        if (wasNear) this.scrollDown();
      } else if (ev.type === 'tool_call_start') {
        const wasNear = this.nearBottom();
        this.messages.push({ role: 'tool', id: ev.id, name: ev.name, status: 'bezig' });
        this.toolChipIdx[ev.id] = this.messages.length - 1;
        this.streamIdx = null;
        if (wasNear) this.scrollDown();
      } else if (ev.type === 'tool_call') {
        // niets — chip staat al op 'bezig'
      } else if (ev.type === 'tool_result') {
        const wasNear = this.nearBottom();
        const k = this.toolChipIdx[ev.id];
        if (k != null && this.messages[k]) {
          this.messages[k].status = (ev.ok === false ? 'fout' : 'klaar');
        } else {
          this.messages.push({ role: 'tool', id: ev.id, name: ev.name || '', status: (ev.ok === false ? 'fout' : 'klaar') });
        }
        this.streamIdx = null;
        if (wasNear) this.scrollDown();
      } else if (ev.type === 'confirmation_required') {
        const wasNear = this.nearBottom();
        this.messages.push({
          role: 'confirm',
          tool_call_id: ev.tool_call_id,
          confirm_token: ev.confirm_token,
          tool_naam: ev.tool_naam,
          preview: ev.preview || { titel: '', regels: [] },
          busy: false,
          done: false,
          result: null,
        });
        this.streamIdx = null;
        if (wasNear) this.scrollDown();
      } else if (ev.type === 'done') {
        // Credits-controle
        if (typeof ev.credits_resterend === 'number') {
          this.creditsLaag = ev.credits_resterend < 5 ? ev.credits_resterend : null;
        }
        if (ev.ai_balance) {
          window.dispatchEvent(new CustomEvent('ai-credits-updated', { detail: ev.ai_balance }));
        }
      } else if (ev.type === 'error') {
        this.error = ev.message || 'Er ging iets mis.';
      }
    },

    async consumeStream(url, formBody) {
      let addedConfirmCard = false;

      try {
        this.abortCtrl = new AbortController();

        const res = await fetch(url, {
          method: 'POST',
          headers: { 'X-CSRF-Token': this.csrf, 'X-Requested-With': 'fetch' },
          body: formBody,
          signal: this.abortCtrl.signal,
        });

        if (res.status === 402) {
          const body = await res.json().catch(() => ({}));
          if (this.streamIdx !== null && this.messages[this.streamIdx] && this.messages[this.streamIdx].text === '') {
            this.messages.splice(this.streamIdx, 1);
          }
          this.streamIdx = null;
          if (body.error === 'cost_cap_reached') {
            this.error = body.message || 'Maandelijkse AI-kosten-limiet bereikt. Neem contact op met je beheerder.';
            // Geen credit-shop openen — die helpt niet bij een cap-hit.
          } else {
            this.error = 'Je hebt geen AI-credits meer. Koop bij om de assistent te gebruiken.';
            window.dispatchEvent(new CustomEvent('open-credit-shop'));
          }
          return;
        }

        if (res.status === 429) {
          if (this.streamIdx !== null && this.messages[this.streamIdx] && this.messages[this.streamIdx].text === '') {
            this.messages.splice(this.streamIdx, 1);
          }
          this.streamIdx = null;
          this.error = 'Even rustig aan — probeer over een minuutje opnieuw.';
          return;
        }

        if (!res.ok || !res.body) {
          if (this.streamIdx !== null && this.messages[this.streamIdx] && this.messages[this.streamIdx].text === '') {
            this.messages.splice(this.streamIdx, 1);
          }
          this.streamIdx = null;
          this.error = 'Er ging iets mis. Probeer het opnieuw.';
          return;
        }

        const reader  = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';

        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          let nl;
          while ((nl = buf.indexOf('\n')) !== -1) {
            const line = buf.slice(0, nl).trim();
            buf = buf.slice(nl + 1);
            if (!line) continue;
            let ev;
            try { ev = JSON.parse(line); } catch { continue; }
            if (ev.type === 'confirmation_required') addedConfirmCard = true;
            this.handleEvent(ev);
          }
        }

        // Lege assistent-bel aan het einde opruimen als er niets inkwam
        const idx = this.streamIdx;
        if (idx !== null && this.messages[idx] && this.messages[idx].role === 'assistant' && this.messages[idx].text === '') {
          this.messages.splice(idx, 1);
          this.streamIdx = null;
          if (!this.error && !addedConfirmCard) {
            this.error = 'Geen antwoord ontvangen. Probeer het opnieuw.';
          }
        }
      } catch (e) {
        if (e?.name === 'AbortError') {
          // Gebruiker stopte bewust — geen foutmelding tonen.
          // Pop lege assistent-bel als er niets was ingekomen.
          if (this.streamIdx !== null && this.messages[this.streamIdx] && this.messages[this.streamIdx].text === '') {
            this.messages.splice(this.streamIdx, 1);
          }
          this.streamIdx = null;
          return;
        }
        // Echte verbindingsfout
        if (this.streamIdx !== null && this.messages[this.streamIdx] && this.messages[this.streamIdx].text === '') {
          this.messages.splice(this.streamIdx, 1);
        }
        this.streamIdx = null;
        this.error = 'Verbindingsfout. Controleer je internetverbinding.';
      }
    },

    async send() {
      const tekst = this.input.trim();
      if (!tekst || this.streaming || !this.gesprekId) return;

      this.error       = '';
      this.input       = '';
      this.toolChipIdx = {};
      this.messages.push({ role: 'user', text: tekst });
      this.messages.push({ role: 'assistant', text: '' });
      this.streamIdx = this.messages.length - 1;
      this.streaming = true;
      // Na eigen bericht: altijd scrollen
      this.$nextTick(() => this.scrollDown());

      try {
        await this.consumeStream(
          `/admin/ai/assistant/${this.gesprekId}/bericht`,
          new URLSearchParams({ bericht: tekst, _token: this.csrf })
        );
      } finally {
        this.streaming = false;
        this.abortCtrl = null;
        if (this.nearBottom()) this.scrollDown();
        this.$nextTick(() => this.$refs.composer?.focus());
      }
    },

    async bevestig(card) {
      if (card.busy || card.done) return;
      card.busy        = true;
      this.error       = '';
      this.toolChipIdx = {};
      this.messages.push({ role: 'assistant', text: '' });
      this.streamIdx = this.messages.length - 1;
      this.streaming = true;

      try {
        await this.consumeStream(
          `/admin/ai/assistant/${this.gesprekId}/bevestig`,
          new URLSearchParams({ tool_call_id: card.tool_call_id, confirm_token: card.confirm_token, _token: this.csrf })
        );
      } finally {
        card.busy    = false;
        card.done    = true;
        card.result  = 'confirmed';
        this.streaming = false;
        this.abortCtrl = null;
        if (this.nearBottom()) this.scrollDown();
        this.$nextTick(() => this.$refs.composer?.focus());
      }
    },

    async annuleer(card) {
      if (card.busy || card.done) return;
      card.busy        = true;
      this.error       = '';
      this.toolChipIdx = {};
      this.messages.push({ role: 'assistant', text: '' });
      this.streamIdx = this.messages.length - 1;
      this.streaming = true;

      try {
        await this.consumeStream(
          `/admin/ai/assistant/${this.gesprekId}/annuleer-toolcall`,
          new URLSearchParams({ tool_call_id: card.tool_call_id, confirm_token: card.confirm_token, _token: this.csrf })
        );
      } finally {
        card.busy    = false;
        card.done    = true;
        card.result  = 'cancelled';
        this.streaming = false;
        this.abortCtrl = null;
        if (this.nearBottom()) this.scrollDown();
        this.$nextTick(() => this.$refs.composer?.focus());
      }
    },

    async uploadBestand(file) {
      if (!file) return;
      if (file.size > 2 * 1024 * 1024) { this.error = 'Bestand is te groot (max 2 MB).'; return; }
      if (!/\.csv$/i.test(file.name)) { this.error = 'Alleen .csv-bestanden zijn toegestaan.'; return; }
      this.error = '';
      const fd = new FormData();
      fd.append('bestand', file);
      fd.append('_token', this.csrf);
      try {
        const res = await fetch(`/admin/ai/assistant/${this.gesprekId}/upload`, {
          method: 'POST',
          headers: { 'X-CSRF-Token': this.csrf, 'X-Requested-With': 'fetch' },
          body: fd,
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          this.error = j.error === 'te_groot' ? 'Bestand is te groot (max 2 MB).'
                     : j.error === 'alleen_csv' ? 'Alleen .csv-bestanden zijn toegestaan.'
                     : j.error === 'leeg_bestand' ? 'Het bestand is leeg.'
                     : 'Upload mislukt. Probeer het opnieuw.';
          return;
        }
        const r = await res.json();
        this.messages.push({
          role: 'upload',
          bestandsnaam: r.bestandsnaam,
          aantal_rijen: r.aantal_rijen,
          aantal_met_fouten: r.aantal_met_fouten,
          upload_id: r.upload_id,
        });
        this.$nextTick(() => this.scrollDown());
        // Pre-fill the composer so the medewerker only needs to mention the dossier
        this.input = `Importeer de geüploade deelnemers (${r.aantal_rijen} rijen, upload_id: ${r.upload_id}) in welk dossier?`;
        this.$nextTick(() => this.$refs.composer?.focus());
      } catch (e) {
        this.error = 'Verbindingsfout bij uploaden. Probeer het opnieuw.';
      }
    },
  };
}
